import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { jwtDecode } from "jwt-decode";
import { clearBootstrappedModules } from "./modulesBootstrapCache";
import {
  clearRefreshToken,
  refreshAccessTokenApi,
  sendPortalEvent,
} from "../helpers/apiHelper";

const AuthContext = createContext();
const MODULE_POPUP_OPENED_PREFIX = "bhp_module_popup_opened::";

const clearModulePopupState = (storage) => {
  if (!storage) return;
  const keysToRemove = [];

  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (typeof key === "string" && key.startsWith(MODULE_POPUP_OPENED_PREFIX)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => storage.removeItem(key));
};

const isTokenValid = (token) => {
  try {
    if (!token) return false;
    const decoded = jwtDecode(token);
    const expMs = decoded.exp * 1000;
    // consider valid if it has > 30s
    return expMs - Date.now() > 30 * 1000;
  } catch {
    return false;
  }
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tokens, setTokens] = useState({ accessToken: null, idToken: null });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  const refreshInterval = useRef(null);
  const refreshInFlight = useRef(false);

  const signOut = useCallback(async () => {
    const idToken =
      localStorage.getItem("idToken") || sessionStorage.getItem("idToken");
    const partyId =
      localStorage.getItem("partyId") || sessionStorage.getItem("partyId");
    const authFlow =
      localStorage.getItem("authFlow") || sessionStorage.getItem("authFlow");

    sendPortalEvent(
      "LOGOUT",
      partyId || null,
      authFlow ? { authFlow } : null,
      idToken || null,
    ).catch(() => {});

    try {
      await clearRefreshToken();
    } catch (err) {
    }

    setUser(null);
    setTokens({ accessToken: null, idToken: null });
    setIsLoggedIn(false);
    setLoading(false);
    setSessionExpired(false);

    localStorage.removeItem("wasLoggedIn");
    localStorage.removeItem("accessToken"); 
    localStorage.removeItem("idToken");
    localStorage.removeItem("clientId");
    localStorage.removeItem("portalUserId");
    localStorage.removeItem("authFlow");
    localStorage.removeItem("partyId");
    localStorage.removeItem("selectedMatterId");
    localStorage.removeItem("matterId");
    localStorage.removeItem("bhp_retainer_pending");
    localStorage.removeItem("bhp_retainer_return_path");

    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("idToken");
    sessionStorage.removeItem("clientId");
    sessionStorage.removeItem("portalUserId");
    sessionStorage.removeItem("authFlow");
    sessionStorage.removeItem("partyId");
    sessionStorage.removeItem("selectedMatterId");
    sessionStorage.removeItem("matterId");
    clearModulePopupState(sessionStorage);
    clearBootstrappedModules();

    if (refreshInterval.current) {
      clearInterval(refreshInterval.current);
      refreshInterval.current = null;
    }
  }, []);

  const refreshAccessToken = useCallback(async () => {
    if (sessionExpired) return;
    if (refreshInFlight.current) return;

    refreshInFlight.current = true;

    try {
      const res = await refreshAccessTokenApi();

      if (res?.access_token) {
        setUser((prev) => ({
          ...(prev || {}),
          email: res["email"],
          cocoId: res["coco_id"],
          phone: res["phone_number"],
        }));

        setTokens((prev) => ({
          ...prev,
          accessToken: res["access_token"],
          idToken: res["id_token"] || prev?.idToken || null,
        }));

        localStorage.setItem("accessToken", res["access_token"]);
        if (res["id_token"]) {
          localStorage.setItem("idToken", res["id_token"]);
        }

        setIsLoggedIn(true);
        return;
      }

      setSessionExpired(true);
      await signOut();
    } catch (err) {
 
      const stillValid = isTokenValid(tokens.accessToken || localStorage.getItem("accessToken"));
      if (!stillValid) {
        setSessionExpired(true);
        await signOut();
      }
    } finally {
      refreshInFlight.current = false;
    }
  }, [signOut, sessionExpired, tokens.accessToken]);

  // Applies a fresh token pair the backend reissued *synchronously* right after an
  // email/phone update (AdminUpdateUserAttributes invalidates the previous refresh token as a
  // Cognito security measure - see backend UpdateCognitoUserServiceAdapter). Call sites: any
  // caller of patchBhpClientContact whose response includes access_token/id_token
  // (ContactUsPage, ContactEditPage). The new refresh token itself never reaches JS - the
  // backend already re-set it as the HttpOnly clientPortalToken cookie via Set-Cookie.
  const applyRefreshedTokens = useCallback((refreshed) => {
    if (!refreshed?.access_token) return;

    setTokens((prev) => ({
      ...prev,
      accessToken: refreshed.access_token,
      idToken: refreshed.id_token || prev?.idToken || null,
    }));

    localStorage.setItem("accessToken", refreshed.access_token);
    if (refreshed.id_token) {
      localStorage.setItem("idToken", refreshed.id_token);
    }

    setSessionExpired(false);
    setIsLoggedIn(true);
  }, []);

  const signIn = (userData, tokenData) => {
    localStorage.setItem("wasLoggedIn", "true");
    setSessionExpired(false);
    setUser(userData);

    const accessToken = tokenData?.AccessToken || null;
    const idToken = tokenData?.IdToken || null;

    setTokens({ accessToken, idToken });
    setIsLoggedIn(true);
    setLoading(false);

    if (accessToken) localStorage.setItem("accessToken", accessToken);
    if (idToken) localStorage.setItem("idToken", idToken);
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);

      const wasLoggedIn = localStorage.getItem("wasLoggedIn");
      const storedAccessToken = localStorage.getItem("accessToken");
      const storedIdToken = localStorage.getItem("idToken");

      if (isTokenValid(storedAccessToken)) {
        setTokens({ accessToken: storedAccessToken, idToken: storedIdToken });
        setIsLoggedIn(true);
        setLoading(false);
        return;
      }

      //Only try refresh if we previously logged in
      if (wasLoggedIn === "true") {

        for (let attempt = 0; attempt < 3; attempt++) {
          await sleep(700 * (attempt + 1));
          const before = localStorage.getItem("accessToken");
          if (isTokenValid(before)) break;

          await refreshAccessToken();

          const after = localStorage.getItem("accessToken");
          if (isTokenValid(after)) break;
        }
      }

      setLoading(false);
    };

    init();
  }, [refreshAccessToken]);

  useEffect(() => {
    if (!tokens.accessToken) {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
        refreshInterval.current = null;
      }
      return;
    }

    if (refreshInterval.current) {
      clearInterval(refreshInterval.current);
      refreshInterval.current = null;
    }

    refreshInterval.current = setInterval(() => {
      try {
        const decoded = jwtDecode(tokens.accessToken);
        const expiresAt = decoded.exp * 1000;
        const now = Date.now();
        const timeLeft = expiresAt - now;

        // refresh when < 2 min
        if (timeLeft < 2 * 60 * 1000) {
          refreshAccessToken();
        }
      } catch (e) {
        console.error("[Auth] Failed to decode access token during interval check", e);
        signOut();
      }
    }, 60 * 1000);

    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
        refreshInterval.current = null;
      }
    };
  }, [tokens.accessToken, refreshAccessToken, signOut]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        tokens,
        isLoggedIn,
        signIn,
        signOut,
        refreshAccessToken,
        applyRefreshedTokens,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
